import type { InspectionEvent } from 'xstate';
import type { InspectorEntry, InspectorStateValue, InspectorTransition } from './protocol';
import { sanitizeInspectionEvent, sanitizeStateValue, sanitizeVoiceLiveContext } from './sanitize';
import type { VoiceLiveContext } from '../machine/types';

interface SnapshotLike {
  context?: unknown;
  status?: unknown;
  value?: unknown;
}

interface TransitionLike {
  actions?: readonly unknown[];
  eventType?: unknown;
  guard?: unknown;
  source?: { id?: unknown };
  target?: ReadonlyArray<{ id?: unknown }>;
}

const isVoiceLiveContext = (value: unknown): value is VoiceLiveContext => {
  if (!value || typeof value !== 'object') return false;
  const context = value as Partial<VoiceLiveContext>;

  return (
    typeof context.retryCount === 'number' &&
    typeof context.sessionReady === 'boolean' &&
    Array.isArray(context.iceServers)
  );
};

const implementationName = (implementation: unknown): string | undefined => {
  if (typeof implementation === 'string') return implementation;
  if (!implementation || typeof implementation !== 'object') return undefined;

  const value = implementation as { type?: unknown };
  return typeof value.type === 'string' ? value.type : undefined;
};

const normalizeTransition = (transition: TransitionLike): InspectorTransition => ({
  actions: (transition.actions ?? [])
    .map(implementationName)
    .filter((action): action is string => Boolean(action)),
  eventType: typeof transition.eventType === 'string' ? transition.eventType : '',
  guard: implementationName(transition.guard),
  source: typeof transition.source?.id === 'string' ? transition.source.id : undefined,
  targets: (transition.target ?? [])
    .map((target) => target.id)
    .filter((target): target is string => typeof target === 'string'),
});

export interface InspectorEntryBuilder {
  /** Returns an entry only on `@xstate.snapshot`; other inspection events accumulate into it. */
  build: (inspectionEvent: InspectionEvent) => InspectorEntry | undefined;
  /** Drop accumulated actions/transitions and the previous-state link (sequence is preserved). */
  reset: () => void;
}

/**
 * Turns the raw XState inspection stream into sanitized {@link InspectorEntry} records.
 * Shared by the BroadcastChannel sender and the App Insights session recorder so a log
 * replayed from production is byte-identical to what the local inspector shows.
 */
export const createInspectorEntryBuilder = (): InspectorEntryBuilder => {
  let actions: string[] = [];
  let lastState: InspectorStateValue | undefined;
  let sequence = 0;
  let transitions: InspectorTransition[] = [];

  const reset = () => {
    actions = [];
    lastState = undefined;
    transitions = [];
  };

  const build = (inspectionEvent: InspectionEvent): InspectorEntry | undefined => {
    if (inspectionEvent.type === '@xstate.action') {
      actions.push(inspectionEvent.action.type);
      return undefined;
    }

    if (inspectionEvent.type === '@xstate.microstep') {
      transitions = inspectionEvent._transitions.map((transition) =>
        normalizeTransition(transition as unknown as TransitionLike),
      );
      return undefined;
    }

    if (inspectionEvent.type !== '@xstate.snapshot') return undefined;

    const snapshot = inspectionEvent.snapshot as SnapshotLike;
    if (!isVoiceLiveContext(snapshot.context)) return undefined;

    const actorRef = inspectionEvent.actorRef as { id?: unknown; sessionId?: unknown };
    const state = sanitizeStateValue(snapshot.value);
    const entry: InspectorEntry = {
      actorId:
        typeof actorRef.id === 'string'
          ? actorRef.id
          : typeof actorRef.sessionId === 'string'
            ? actorRef.sessionId
            : inspectionEvent.rootId,
      actions: [...actions],
      context: sanitizeVoiceLiveContext(snapshot.context),
      event: sanitizeInspectionEvent(inspectionEvent.event),
      previousState: lastState,
      sequence: sequence++,
      state,
      status: typeof snapshot.status === 'string' ? snapshot.status : 'unknown',
      timestamp: Date.now(),
      transitions: [...transitions],
    };

    lastState = state;
    actions = [];
    transitions = [];

    return entry;
  };

  return { build, reset };
};
